import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Form,
  Input,
  Row,
  Button,
  message,
  Select,
  notification,
  Spin,
  List,
  Avatar,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { API } from "../../config/api";
import { GET, POST, PUT } from "../../utils/apicalls";
import PageHeader from "../../admin/pageHeader/pageheader";

const { TextArea } = Input;

const ViewTaskData: React.FC = () => {
  const [taskData, setTaskData] = useState<any>({});
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchTaskData();
      fetchComments();
    }
  }, [id]);

  // Fetch Task Data
  const fetchTaskData = async () => {
    try {
      setLoading(true);
      const response: any = await GET(`${API.TASK_ONE}${id}`, {});
      if (response?.data) {
        setTaskData(response.data);
        form.setFieldsValue(response.data);
      }
    } catch (error: any) {
      console.error("Error fetching task data:", error);
      message.error("Failed to fetch task data.");
    } finally {
      setLoading(false);
    }
  };

  // Save Task Data
  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        descriptions: values.descriptions,
        status: values.status,
        hourss: Number(values.hourss || 0),
        minituess: Number(values.minituess || 0),
      };
      const response = await PUT(`${API.UPDATE_TASK}${id}`, payload);
      if (response) {
        notification.success({ message: "Task updated successfully!" });
        navigate(-1);
      }
    } catch (error: any) {
      console.error("Error updating task:", error);
      notification.error({ message: "Failed to update task." });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Comments
  const fetchComments = async () => {
    try {
      setLoading(true);
      const payload = {
        query: "",
        page: 1,
        take: 50,
        taskid: Number(id),
      };
      const response: any = await POST(API.GET_ALL_COMMENTS, payload);
      if (response?.data) {
        setComments(response.data);
      }
    } catch (error: any) {
      console.error("Error fetching comments:", error);
      message.error("Failed to fetch comments.");
    } finally {
      setLoading(false);
    }
  };

  // Handle New Comment Submission
  const handleCommentSubmit = async (values: any) => {
    try {
      const payload = {
        employeeId: 8,
        commets: values.commets,
        date: moment().format("YYYY-MM-DD"),
        projectId: taskData.Projectid,
        clientId: taskData.clientid,
        taskid: id,
      };
      const response = await POST(API.CREATE_COMMENTS, payload);
      if (response) {
        notification.success({ message: "Comment added successfully!" });
        fetchComments(); // Refresh comments
      }
    } catch (error: any) {
      console.error("Error submitting comment:", error);
      notification.error({ message: "Failed to submit comment." });
    }
  };

  return (
    <>
      <PageHeader />
      <Spin spinning={loading}>
        <Card style={{ marginBottom: "16px" }}>
          <Form form={form} onFinish={handleSave} layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <h4>Task Name</h4>
                <p>{taskData?.TaskName || "No task name available."}</p>
              </Col>
              <Col xs={24} md={6}>
                <h4>Description</h4>
                <p>{taskData?.descriptions || "No description available."}</p>
              </Col>
              <Col xs={24} md={6}>
                <h4>Start Date</h4>
                <p>
                  {taskData?.startDates
                    ? moment(taskData.startDates).format("YYYY-MM-DD")
                    : "Not available."}
                </p>
              </Col>
              <Col xs={24} md={6}>
                <h4>End Date</h4>
                <p>
                  {taskData?.endDates
                    ? moment(taskData.endDates).format("YYYY-MM-DD")
                    : "Not available."}
                </p>
              </Col>
            </Row>
            <Form.Item
              name="descriptions"
              label="Descriptions"
              rules={[{ required: true, message: "Enter descriptions!" }]}
            >
              <TextArea placeholder="Enter descriptions" />
            </Form.Item>
            <Form.Item
              name="hourss"
              label="Hours"
              rules={[{ required: true, message: "Enter hours!" }]}
            >
              <Input type="number" placeholder="Enter hours" />
            </Form.Item>
            <Form.Item
              name="minituess"
              label="Minutes"
              rules={[{ required: true, message: "Enter minutes!" }]}
            >
              <Input type="number" placeholder="Enter minutes" />
            </Form.Item>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Select a status!" }]}
            >
              <Select placeholder="Select status">
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="inprogress">inprogress</Select.Option>
                <Select.Option value="completed">Completed</Select.Option>
              </Select>
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Save
            </Button>
          </Form>
        </Card>

        <Card>
          <h2 style={{ textAlign: "center" }}> Comments</h2>
          <List
            dataSource={comments}
            renderItem={(comment) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Avatar>
                      {comment?.empid
                        ? comment?.empid?.fullName
                        : comment?.client?.companyName?.[0] || "U"}
                    </Avatar>
                  }
                  title={
                    comment?.empid
                      ? comment?.empid?.fullName
                      : comment?.client?.companyName || "Anonymous"
                  }
                  description={comment?.commets}
                />
              </List.Item>
            )}
          />
          <Form onFinish={handleCommentSubmit} layout="vertical">
            <Form.Item
              name="commets"
              rules={[{ required: true, message: "Enter a comment!" }]}
            >
              <TextArea placeholder="Enter your comment" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block>
              Submit Comment
            </Button>
          </Form>
        </Card>
      </Spin>
    </>
  );
};

export default ViewTaskData;
